import { Icon } from '@amelio/ui-library';
import { createContext, useContext, useMemo } from 'react';

import type { PerformanceProFeatureValue } from '@/constants/PerformanceProFeature';
import type { ComponentProps, ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { requestPerformanceUpgrade } from '@/utils/requestPerformanceUpgrade';

export interface PlanLock {
    /**
     * The screen's subject is a Performance PRO feature the company no longer has: its data stays
     * readable, and every action on it is shown locked instead of acting.
     */
    isLocked: boolean;
    /** What a locked action does: offer the upgrade. */
    onLocked: () => void;
}

const UNLOCKED: PlanLock = { isLocked: false, onLocked: () => undefined };

const PlanLockContext = createContext<PlanLock>(UNLOCKED);

interface PlanLockProviderProps {
    isLocked: boolean;
    feature: PerformanceProFeatureValue;
    children: ReactNode;
}

/**
 * Sets, for a whole screen, whether its actions are locked by the plan. Components shared with
 * screens that never set it (meetings, templates) read "not locked" and behave as they always did.
 */
export const PlanLockProvider = ({ isLocked, feature, children }: PlanLockProviderProps) => {
    const value = useMemo(
        () => ({ isLocked, onLocked: () => requestPerformanceUpgrade(feature) }),
        [isLocked, feature],
    );

    return <PlanLockContext.Provider value={value}>{children}</PlanLockContext.Provider>;
};

/** Whether the screen's actions are locked by the plan, and how a locked action offers the upgrade. */
export const usePlanLock = (): PlanLock => useContext(PlanLockContext);

interface PlanLockIconProps {
    className?: string;
    color?: ComponentProps<typeof Icon>['color'];
}

/** The lock an action shows while the plan locks it. */
export const PlanLockIcon = ({ className, color }: PlanLockIconProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Icon
            name={'security-lock'}
            ariaLabel={t('performancePro.locked')}
            className={className}
            color={color}
        />
    );
};
